<template>
  <div class="table-scroll-wrapper">
    <div
      v-if="filteredProducts.length === 0"
      class="no-results"
    >
      <p>{{ $t('PQTOffersView.noProductsFound') }}</p>
    </div>
    <table
      v-else
      class="table table-bordered table-fixed-first-column"
      aria-label="Price comparison table"
    >
      <thead>
        <!-- Company Names Row -->
        <tr class="company-header-row">
          <th
            class="fixed-col empty-header"
            scope="col"
          />
          <template v-for="(company, index) in companies">
            <th
              :key="company.name"
              :class="['company-header', {'best-total': company.mainRank === 1}]"
              colspan="2"
              scope="col"
              :aria-label="company.name + (company.rejected ? ' (Rejected)' : '')"
            >
              <div class="company-name">
                {{ company.name }}
                <span
                  v-if="company.rejected"
                  class="rejected-badge"
                  aria-label="Rejected"
                >{{ $t('PQTOffersView.rejected') }}</span>
              </div>
            </th>
          </template>
        </tr>
        <!-- Column Headers Row -->
        <tr class="column-headers-row">
          <th
            class="fixed-col"
            scope="col"
          >
            {{ $t('mhsullar') }}
          </th>
          <template v-for="(company, companyIndex) in companies">
            <th
              :key="`${company.name}-price`"
              class="text-center"
              colspan="2"
              scope="col"
            >
              {{ $t('PQTOffersView.price') }}
            </th>
          </template>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(product, productIndex) in filteredProducts"
          :key="productIndex"
        >
          <td
            class="fixed-col product-cell"
            scope="row"
          >
            <div>{{ product.name }}</div>
            <div
              v-if="product.quantity"
              class="product-quantity"
            >
              ✓ {{ product.quantity }} {{ $t('PQTOffersView.pieces') }}
            </div>
          </td>

          <template v-for="(company, companyIndex) in companies">
            <td
              :key="`${company.name}-${productIndex}-price`"
              class="text-center"
              colspan="2"
              :class="{
                'best-price-main': isBestPrice(company.name, productIndex, 'main'),
                'best-price-alt': isBestPrice(company.name, productIndex, 'alternative')
              }"
            >
              <div class="price-value">
                <div v-if="company.products[productIndex]?.noOffer">
                  <span class="no-offer-badge">
                    <i class="fa fa-ban mr-1"></i> Təklif yoxdur
                  </span>
                </div>
                <div v-else>
                  <strong>{{ $t('PQTOffersView.main') }}</strong> {{ getFormattedMainPrice(company.name, productIndex) }}
                  AZN
                  <span v-if="getProductValue(company.name, productIndex, 'main', 'vat') !== '-'">
                    | {{ $t('PQTOffersView.vat') }} {{ getProductValue(company.name, productIndex, 'main', 'vat') }} AZN
                  </span>
                  <span v-if="getProductValue(company.name, productIndex, 'main', 'total') !== '-'">
                    | {{ $t('PQTOffersView.total') }} {{ getProductValue(company.name, productIndex, 'main', 'total') }} AZN
                  </span>
                  <span
                    v-if="getMainRank(companyIndex, productIndex)"
                    class="rank-badge"
                    :class="`rank-${getMainRank(companyIndex, productIndex)}`"
                  >
                    {{ $t('PQTOffersView.main').replace(':', '') }}-{{ getMainRank(companyIndex, productIndex) }}
                  </span>
<!--                  <span-->
<!--                    v-if="getPriceDifference(company.name, productIndex, 'main')"-->
<!--                    v-b-tooltip.hover-->
<!--                    class="price-difference"-->
<!--                    :class="{'positive': isPriceDifferencePositive(company.name, productIndex, 'main')}"-->
<!--                    :title="$t('PQTOffersView.priceDifferenceExplanation') || 'Qiymət fərqi: Bu qiymət ən yaxşı qiymətdən nə qədər yüksəkdir'"-->
<!--                  >-->
<!--                    {{ getPriceDifference(company.name, productIndex, 'main') }} <i-->
<!--                      class="fa fa-info-circle fa-xs ml-1"-->
<!--                    />-->
<!--                  </span>-->
                </div>
                <div v-if="!company.products[productIndex]?.noOffer">
                  <strong>{{ $t('PQTOffersView.alternative') }}</strong>
                  {{ getProductValue(company.name, productIndex, 'alternative', 'price') }} AZN
                  <span v-if="getProductValue(company.name, productIndex, 'alternative', 'vat') !== '-'">
                    | {{ $t('PQTOffersView.vat') }} {{
                      getProductValue(company.name, productIndex, 'alternative', 'vat')
                    }} AZN
                  </span>
                  <span v-if="getProductValue(company.name, productIndex, 'alternative', 'total') !== '-'">
                    | {{
                      $t('PQTOffersView.total')
                    }} {{ getProductValue(company.name, productIndex, 'alternative', 'total') }} AZN
                  </span>
                  <span
                    v-if="getAlternativeRank(companyIndex, productIndex)"
                    class="alt-rank-badge"
                    :class="`rank-${getAlternativeRank(companyIndex, productIndex)}`"
                  >
                    {{
                      $t('PQTOffersView.alternative').replace(':', '')
                    }}-{{ getAlternativeRank(companyIndex, productIndex) }}
                  </span>
<!--                  <span-->
<!--                    v-if="getPriceDifference(company.name, productIndex, 'alternative')"-->
<!--                    v-b-tooltip.hover-->
<!--                    class="price-difference"-->
<!--                    :class="{'positive': isPriceDifferencePositive(company.name, productIndex, 'alternative')}"-->
<!--                    :title="$t('PQTOffersView.priceDifferenceExplanation') || 'Qiymət fərqi: Bu qiymət ən yaxşı qiymətdən nə qədər yüksəkdir'"-->
<!--                  >-->
<!--                    {{ getPriceDifference(company.name, productIndex, 'alternative') }} <i-->
<!--                      class="fa fa-info-circle fa-xs ml-1"-->
<!--                    />-->
<!--                  </span>-->
                </div>
              </div>
            </td>
          </template>
        </tr>
      </tbody>

      <tfoot>
        <tr class="footer-row">
          <td class="fixed-col">
            <strong>{{ $t('RFQOffersView.toplam') }}</strong>
          </td>
          <template v-for="(company, companyIndex) in companies">
            <td
              :key="`${company.name}-footer`"
              colspan="2"
              class="text-center"
            >
              <div>
                <strong>{{ $t('PQTOffersView.main') }}</strong>
                {{ company.mainTotal === 0 ? '-' : company.mainTotal.toFixed(2) }} AZN
                <span
                  v-if="company.mainRank <= 3"
                  class="footer-rank-badge"
                  :class="`rank-${company.mainRank}`"
                >
                  {{ $t('PQTOffersView.main').replace(':', '') }}: {{ $t(`PQTOffersView.rank${company.mainRank}`) }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >{{
                  $t('PQTOffersView.main').replace(':', '')
                }}: {{ $t('PQTOffersView.rankOther', {rank: company.mainRank}) }}</span>
              </div>
              <div>
                <strong>{{ $t('PQTOffersView.alternative') }}</strong> {{ company.alternativeTotal.toFixed(2) }} AZN
                <span
                  v-if="company.alternativeRank <= 3"
                  class="footer-rank-badge"
                  :class="`rank-${company.alternativeRank}`"
                >
                  {{
                    $t('PQTOffersView.alternative').replace(':', '')
                  }}: {{ $t(`PQTOffersView.rank${company.alternativeRank}`) }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >{{
                  $t('PQTOffersView.alternative').replace(':', '')
                }}: {{ $t('PQTOffersView.rankOther', {rank: company.alternativeRank}) }}</span>
              </div>
            </td>
          </template>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
export default {
  name: "PriceComparisonTable",
  props: {
    products: {
      type: Array,
      required: true
    },
    companies: {
      type: Array,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    rankedOffers: {
      type: Array,
      required: true
    }
  },
  computed: {
    filteredProducts() {
      if (!this.searchQuery.trim()) {
        return this.products;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.products.filter(product =>
          product.name.toLowerCase().includes(query)
      );
    },

    // Calculate best prices for each product
    bestPrices() {
      const bestPrices = {
        main: {},
        alternative: {}
      };

      this.products.forEach((product, productIndex) => {
        let bestMainPrice = Infinity;
        let bestAltPrice = Infinity;

        this.companies.forEach(company => {
          const productData = company.products[productIndex];
          if (productData) {
            // Skip noOffer products when calculating best prices
            if (!productData.noOffer && productData.main && productData.main.price && productData.main.price > 0 && productData.main.price < bestMainPrice) {
              bestMainPrice = productData.main.price;
            }
            if (!productData.noOffer && productData.alternative && productData.alternative.price && productData.alternative.price > 0 && productData.alternative.price < bestAltPrice) {
              bestAltPrice = productData.alternative.price;
            }
          }
        });

        bestPrices.main[productIndex] = bestMainPrice !== Infinity ? bestMainPrice : null;
        bestPrices.alternative[productIndex] = bestAltPrice !== Infinity ? bestAltPrice : null;
      });

      return bestPrices;
    }
  },
  methods: {
    getFormattedMainPrice(companyName, productIndex) {
      const company = this.companies.find(c => c.name === companyName);
      if (!company || !company.products[productIndex]) return '-';

      // Check if this is a "no offer" product
      if (company.products[productIndex].noOffer) {
        return 'Təklif yoxdur';
      }

      const value = company.products[productIndex]['main']?.['price'];
      if (value === 0) return '-';
      return value !== undefined && value !== null ? value.toFixed(2) : '-';
    },

    getMainRank(companyIndex, productIndex) {
      const offer = this.rankedOffers[companyIndex];
      if (!offer || !offer.products) return null;

      const matched = offer.products[productIndex];
      return matched?.globalRank || null;
    },

    getAlternativeRank(companyIndex, productIndex) {
      const offer = this.rankedOffers[companyIndex];
      if (!offer || !offer.products) return null;

      const matched = offer.products[productIndex];
      return matched?.alternativeOffer?.globalRank || null;
    },

    getProductValue(companyName, productIndex, type, field) {
      const company = this.companies.find(c => c.name === companyName);
      if (!company || !company.products[productIndex]) return '-';

      // Check if this is a "no offer" product
      if (company.products[productIndex].noOffer && type === 'main') {
        return '-';
      }

      const value = company.products[productIndex][type]?.[field];
      return value !== undefined && value !== null ? value.toFixed(2) : '-';
    },

    isBestPrice(companyName, productIndex, type) {
      const company = this.companies.find(c => c.name === companyName);
      if (!company || !company.products[productIndex]) return false;

      // No offer products can't be best price
      if (company.products[productIndex].noOffer) return false;

      const price = company.products[productIndex][type]?.price;
      const bestPrice = this.bestPrices[type][productIndex];

      return price !== undefined && price !== null && price > 0 && bestPrice !== null && bestPrice > 0 && price === bestPrice;
    },

    getPriceDifference(companyName, productIndex, type) {
      const company = this.companies.find(c => c.name === companyName);
      if (!company || !company.products[productIndex]) return null;

      // No offer products don't have price difference
      if (company.products[productIndex].noOffer) return null;

      const price = company.products[productIndex][type]?.price;
      const bestPrice = this.bestPrices[type][productIndex];

      if (price === undefined || price === null || price === 0 || bestPrice === null || bestPrice === 0 || price === bestPrice) {
        return null;
      }

      // Calculate how much higher this price is compared to the best price
      // Formula: ((current price - best price) / best price) * 100
      const diff = ((price - bestPrice) / bestPrice) * 100;
      return `+${diff.toFixed(2)}%`;
    },

    isPriceDifferencePositive(companyName, productIndex, type) {
      const company = this.companies.find(c => c.name === companyName);
      if (!company || !company.products[productIndex]) return false;

      // No offer products don't have price difference
      if (company.products[productIndex].noOffer) return false;

      const price = company.products[productIndex][type]?.price;
      const bestPrice = this.bestPrices[type][productIndex];

      if (price === undefined || price === null || price === 0 || bestPrice === null || bestPrice === 0) {
        return false;
      }

      return price > bestPrice;
    }
  }
};
</script>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
  max-height: 75vh;
  position: relative;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.no-results {
  padding: 30px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin: 20px 0;
}

.no-results p {
  font-size: 16px;
  color: #6c757d;
  margin: 0;
}

.table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
  margin-bottom: 0;
}

.table th, .table td {
  padding: 1px 2px;
  border: 1px solid #DEE2E6;
  vertical-align: middle;
}

.table td[colspan="2"] {
  min-width: 240px;
}

.fixed-col {
  position: sticky;
  left: 0;
  background-color: #FFFFFF;
  z-index: 3;
  border-right: 2px solid #DEE2E6;
  box-shadow: 2px 0 5px -2px rgba(0, 0, 0, 0.1);
  min-width: 220px;
}

.company-header-row th {
  height: 10px;
  background-color: #F8F9FA;
}

.company-header {
  text-align: center;
  padding: 12px 0 0 0;
  min-width: 240px;
  transition: background-color 0.2s;
}

.company-name {
  font-size: 15px;
  font-weight: 600;
  color: #343A40;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  max-width: 240px;
}

.rejected-badge {
  font-size: 12px;
  font-weight: bold;
  color: #DC3545;
  margin-left: 5px;
  padding: 2px 5px;
  border: 1px solid #DC3545;
  border-radius: 3px;
}

.company-total {
  font-size: 18px;
  font-weight: bold;
  color: #212529;
  margin-top: 4px;
}

.company-rank {
  font-size: 12px;
  color: #6C757D;
  margin-top: 4px;
  margin-bottom: 4px;
}

.section-header-row {
  background-color: #E9ECEF;
  height: 40px;
}

.section-header-row th {
  font-weight: bold;
  font-size: 14px;
  color: #212529;
  padding-left: 16px;
  text-align: left;
}

.column-headers-row th {
  height: 20px;
  background-color: #F8F9FA;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  text-align: center;
}

.product-cell {
  padding: 2px 3px;
  font-weight: 500;
  color: #212529;
  transition: background-color 0.2s;
}

.product-quantity {
  font-size: 13px;
  color: #28A745;
  margin-top: 4px;
}

.price-value {
  font-size: 15px;
  font-weight: normal;
  color: #212529;
  position: relative;
  padding-left: 35px;
  line-height: 1.0;
}

.rank-badge {
  position: absolute;
  top: -1px;
  left: -2px;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  padding: 0 8px;
  white-space: nowrap;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.alt-rank-badge {
  position: absolute;
  top: 28px;
  left: -2px;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  padding: 0 8px;
  white-space: nowrap;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.rank-1 {
  background-color: #28A745;
}

.rank-2 {
  background-color: #FFC107;
}

.rank-3 {
  background-color: #DC3545;
}

.rank-4, .rank-5, .rank-6, .rank-7, .rank-8, .rank-9, .rank-10, .rank-11, .rank-12, .rank-13, .rank-14, .rank-15, .rank-16, .rank-17, .rank-18, .rank-19, .rank-20, .rank-21, .rank-22, .rank-23 {
  background-color: #6C757D;
}

.price-difference {
  font-size: 12px;
  font-weight: normal;
  margin-top: 4px;
  display: inline-block;
  padding: 1px 4px;
  border-radius: 3px;
}

.price-difference.positive {
  color: #DC3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.price-difference:not(.positive) {
  color: #198754;
  background-color: rgba(25, 135, 84, 0.1);
}

.no-offer-badge {
  display: inline-block;
  background-color: #6c757d;
  color: white;
  font-size: 0.85rem;
  padding: 5px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.best-price-main {
  background-color: #E6F4EA;
}

.best-price-alt {
  background-color: #E3F2FD;
}

.best-price-main.best-price-alt {
  background: linear-gradient(135deg, #E8F5E9 50%, #E3F2FD 50%);
}

.best-total {
  background-color: #FFF3CD;
}

.footer-row {
  height: 45px;
  background-color: #F8F9FA;
  border-top: 2px solid #CED4DA;
}

.footer-row td {
  font-size: 15px;
  font-weight: normal;
}

.text-success {
  color: #28A745 !important;
}

.text-muted {
  color: #6C757D !important;
}

.footer-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
  margin-left: 8px;
  padding: 0 8px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Responsive styles */
@media (max-width: 576px) {
  .rank-badge, .alt-rank-badge {
    left: -10px;
    font-size: 9px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
  }

  .price-value {
    font-size: 13px;
    padding-left: 25px;
  }

  .fixed-col {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .company-header {
    min-width: 200px;
  }

  .rank-badge, .alt-rank-badge {
    left: -15px;
    font-size: 10px;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
  }

  .table td[colspan="2"] {
    min-width: 200px;
  }

  .price-value {
    padding-left: 30px;
  }
}
</style>
